import React, { useMemo, useState } from "react";
import { Box, CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { useUserRoleStore } from "../../../services/api/Wallet/useUserRoleStore";
import { useTalentWallet } from "../../../services/api/Wallet/useTalentWallet";
import { useCurrentTalentProfile } from "../../../hooks/useProfile";

import TalentWalletCard from "./TalentWalletCard";
import { TalentWalletChartSection } from "./TalentWalletChartSection";
import { TalentWalletBookingTableSection } from "./TalentWalletBookingTableSection";
import { StatsSummaryCard } from "./StatsSummaryCard";
import ScrollToTop from "./ScrollToTop";
import { useGetTalentEarnings } from "../../../services/api/user.api";
import { useParams } from "react-router-dom";

const themeColors = {
  talent: "#6A4EFF",
  business: "#00BFA5",
  admin: "#455A64",
};

export default function TalentWalletDashboardNew() {
  const [darkMode] = useState(true); // Always use dark mode
  const { role } = useUserRoleStore();
  const { id } = useParams();
  const { currentProfile } = useCurrentTalentProfile();
  const talentId = currentProfile?.id;
  const { data, isLoading, syncToMainWallet } = useTalentWallet(talentId || "");

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: themeColors[role] },
          secondary: { main: "#FF7043" },
        },
        typography: { fontFamily: "Lexend, sans-serif" },
        shape: { borderRadius: 12 },
      }),
    [darkMode, role]
  );

  console.log(talentId, "talentId", id, "talent is from user");

  const bookings = Array.isArray(data?.bookings)
    ? data.bookings.map((b: any, i: number) => {
        const mapped = {
          date: b?.date || "-",
          session: b?.session || "N/A",
          client: b?.client || "N/A",
          chips: typeof b?.chips === "number" ? b.chips : 0,
          duration: b?.duration || "",
          mode: b?.mode || "live",
          chipType: b?.chipType || "virtual",
        };
        // console.log(`✅ Booking[${i}]`, mapped); // log each mapped entry
        return mapped;
      })
    : [];

  if (!Array.isArray(data?.bookings)) {
    console.warn("⚠️ data.bookings is not an array. Value:", data?.bookings);
  }

  const { data: talentEarnings } = useGetTalentEarnings({
    key: ["talent-earnings"],
  });

  console.log(talentEarnings, "talentEarnings");

  const numericTalentId = Number(id) || talentId;

  const chipsFromEarnings = useMemo(() => {
    if (!Array.isArray(talentEarnings?.data)) return 0;

    const matchingEarning = talentEarnings?.data?.find(
      (e: any) => Number(e?.talent?.id) === numericTalentId
    );

    console.log("🎯 URL Talent ID:", numericTalentId);
    console.log("🔍 Matched Earning:", matchingEarning);
    console.log("💰 Chips From Earnings:", matchingEarning?.chips ?? 0);

    return matchingEarning?.chips ?? 0;
  }, [talentEarnings?.data, numericTalentId]);

  // —— Netflix-ish tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.75)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />
      <Box
        p={{ xs: 2, md: 4 }}
        maxWidth="1200px"
        mx="auto"
        sx={{
          backgroundColor: COLORS.bg,
          color: COLORS.text,
          minHeight: "100vh",
        }}
      >
        <TalentWalletCard
          earnedChips={chipsFromEarnings}
          isLoading={isLoading}
          bookings={bookings}
          onSync={async (amount) => {
            try {
              await syncToMainWallet(amount); // this should be a `mutateAsync` or wrapped mutation
              console.log("amount to be send", amount);
              return { success: true };
            } catch (err) {
              return {
                success: false,
                message: "Something went wrong during sync",
              };
            }
          }}
        />
        {/* Stats summary */}
        <StatsSummaryCard
          totalChips={chipsFromEarnings}
          thisMonthChips={chipsFromEarnings}
          averageChips={
            bookings.length > 0
              ? Math.round(chipsFromEarnings / bookings.length)
              : 0
          }
          bonusChips={bookings
            .filter((b: any) => b.chipType === "bonus")
            .reduce((sum: any, b: any) => sum + b.chips, 0)}
          thisWeekChips={chipsFromEarnings}
        />

        {/* Chart + Booking Table */}
        <TalentWalletChartSection
          bookings={bookings}
          primaryColor={themeColors[role]}
        />
        <TalentWalletBookingTableSection
          bookings={bookings}
          chipsFromEarnings={chipsFromEarnings}
        />
      </Box>
    </ThemeProvider>
  );
}
